import React from "react";
import { Button, Form, Row, Col, Badge } from "react-bootstrap";

// Phase 4B, §18/§26. A controlled, ordered stage-list editor — the parent
// (TaskApprovalPolicyEditor) owns the actual save call (§18's "transactional
// replacement" PUT .../stages), this component only edits the in-memory
// array. Approver source types are resolution STRATEGIES, never a
// hardcoded name/role — see PHASE_4B_APPROVAL_POLICY_MODEL.md §2.
const APPROVER_SOURCE_TYPES = [
  "FIXED_USER", "TASK_ASSIGNEE_MANAGER", "TASK_CREATOR_MANAGER", "PROJECT_OWNER",
  "PROJECT_MANAGER", "MODULE_OWNER", "ROLE", "TASK_ASSIGNEE", "TASK_CREATOR",
];
const PROJECT_MEMBERSHIP_TYPES = ["OWNER", "MANAGER", "MEMBER", "REVIEWER", "TESTER", "OBSERVER"];

const emptyStage = (order) => ({
  stage_order: order,
  stage_name: `Stage ${order}`,
  approver_source_type: "FIXED_USER",
  fixed_user_id: "",
  role_id: "",
  project_membership_type: "",
  allow_any_matching_approver: true,
  minimum_approvals: 1,
  requires_remarks_on_approve: false,
  requires_remarks_on_reject: true,
  requires_remarks_on_changes_requested: true,
  is_active: true,
});

export default function TaskApprovalStageEditor({ stages, onChange, users, roles, readOnly }) {
  const updateStage = (index, patch) => {
    const next = stages.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(next);
  };

  const addStage = () => {
    onChange([...stages, emptyStage(stages.length + 1)]);
  };

  const removeStage = (index) => {
    const next = stages.filter((_, i) => i !== index).map((s, i) => ({ ...s, stage_order: i + 1 }));
    onChange(next);
  };

  const moveStage = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= stages.length) return;
    const next = [...stages];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((s, i) => ({ ...s, stage_order: i + 1 })));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Approval Stages (in order)</strong>
        {!readOnly && <Button size="sm" variant="outline-primary" onClick={addStage}>+ Add Stage</Button>}
      </div>

      {!stages.length && <div className="text-muted small mb-2">No stages configured yet — a policy needs at least one active stage before it can be activated.</div>}

      {stages.map((stage, index) => (
        <div key={index} className="border rounded p-2 mb-2">
          <Row className="g-2">
            <Col md={1} className="d-flex flex-column justify-content-center align-items-center">
              <Badge bg="light" text="dark">#{stage.stage_order}</Badge>
              {!readOnly && (
                <div className="d-flex flex-column mt-1">
                  <Button size="sm" variant="link" className="py-0" disabled={index === 0} onClick={() => moveStage(index, -1)}>↑</Button>
                  <Button size="sm" variant="link" className="py-0" disabled={index === stages.length - 1} onClick={() => moveStage(index, 1)}>↓</Button>
                </div>
              )}
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-0">Stage Name</Form.Label>
              <Form.Control size="sm" disabled={readOnly} value={stage.stage_name} onChange={(e) => updateStage(index, { stage_name: e.target.value })} />
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-0">Approver Source</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={stage.approver_source_type} onChange={(e) => updateStage(index, { approver_source_type: e.target.value })}>
                {APPROVER_SOURCE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </Form.Select>
            </Col>

            {stage.approver_source_type === "FIXED_USER" && (
              <Col md={3}>
                <Form.Label className="small mb-0">Fixed User</Form.Label>
                <Form.Select size="sm" disabled={readOnly} value={stage.fixed_user_id} onChange={(e) => updateStage(index, { fixed_user_id: e.target.value })}>
                  <option value="">Select...</option>
                  {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Form.Select>
              </Col>
            )}
            {stage.approver_source_type === "ROLE" && (
              <Col md={3}>
                <Form.Label className="small mb-0">Role</Form.Label>
                <Form.Select size="sm" disabled={readOnly} value={stage.role_id} onChange={(e) => updateStage(index, { role_id: e.target.value })}>
                  <option value="">Select...</option>
                  {(roles || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Form.Select>
              </Col>
            )}
            {["PROJECT_OWNER", "PROJECT_MANAGER", "MODULE_OWNER"].includes(stage.approver_source_type) && (
              <Col md={3}>
                <Form.Label className="small mb-0">Membership Type (optional)</Form.Label>
                <Form.Select size="sm" disabled={readOnly} value={stage.project_membership_type || ""} onChange={(e) => updateStage(index, { project_membership_type: e.target.value })}>
                  <option value="">(default for this source)</option>
                  {PROJECT_MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
              </Col>
            )}

            <Col md={2}>
              <Form.Label className="small mb-0">Minimum Approvals</Form.Label>
              <Form.Control size="sm" type="number" min={1} disabled={readOnly} value={stage.minimum_approvals} onChange={(e) => updateStage(index, { minimum_approvals: Number(e.target.value) || 1 })} />
            </Col>
            <Col md={10} className="d-flex align-items-end gap-3 flex-wrap">
              <Form.Check inline type="checkbox" label="Remarks required on Approve" disabled={readOnly} checked={!!stage.requires_remarks_on_approve} onChange={(e) => updateStage(index, { requires_remarks_on_approve: e.target.checked })} />
              <Form.Check inline type="checkbox" label="Remarks required on Reject" disabled={readOnly} checked={!!stage.requires_remarks_on_reject} onChange={(e) => updateStage(index, { requires_remarks_on_reject: e.target.checked })} />
              <Form.Check inline type="checkbox" label="Remarks required on Changes Requested" disabled={readOnly} checked={!!stage.requires_remarks_on_changes_requested} onChange={(e) => updateStage(index, { requires_remarks_on_changes_requested: e.target.checked })} />
              <Form.Check inline type="checkbox" label="Active" disabled={readOnly} checked={stage.is_active !== false} onChange={(e) => updateStage(index, { is_active: e.target.checked })} />
              {!readOnly && <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => removeStage(index)}>Remove</Button>}
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
}

export { APPROVER_SOURCE_TYPES, PROJECT_MEMBERSHIP_TYPES };
